import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle, AlertCircle, Clock, Loader, Calendar, User, FileText, Trash2 } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';

interface WorkflowHistoryDetailModalProps {
  workflowId: string;
  onClose: () => void;
}

const WorkflowHistoryDetailModal: React.FC<WorkflowHistoryDetailModalProps> = ({ workflowId, onClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'steps' | 'events'>('overview');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['workflow-history-detail', workflowId],
    queryFn: () => api.getWorkflowHistoryDetails(workflowId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteWorkflow(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-history'] });
      onClose();
    },
  });

  const workflow = data?.workflow;
  const steps = data?.steps || [];
  const events = data?.events || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-400';
      case 'FAILED':
        return 'text-red-400';
      case 'PROCESSING':
        return 'text-orange-400';
      default:
        return 'text-blue-400';
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'FAILED':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'PROCESSING':
        return <Loader className="w-5 h-5 text-orange-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-blue-400" />;
    }
  };

  const formatDuration = (start?: string, end?: string) => {
    if (!start) return 'N/A';
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const durationMs = endTime.getTime() - startTime.getTime();

    const minutes = Math.floor(durationMs / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div>
            <h2 className="text-2xl font-bold text-white">Workflow History Details</h2>
            <p className="text-sm text-gray-400 mt-1">ID: {workflowId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 px-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('steps')}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'steps'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Steps ({steps.length})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'events'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Events ({events.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && workflow && (
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Status</p>
                        <p className={`text-xl font-bold ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Current Step</p>
                        <p className="text-xl font-bold text-white">{workflow.current_step || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <User className="w-4 h-4" />
                        <span className="text-sm">Customer</span>
                      </div>
                      <p className="text-white font-medium">{workflow.customer_id}</p>
                    </div>

                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <User className="w-4 h-4" />
                        <span className="text-sm">Admin Account</span>
                      </div>
                      <p className="text-white font-medium">{workflow.admin_account || 'N/A'}</p>
                    </div>

                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">Created At</span>
                      </div>
                      <p className="text-white font-medium">
                        {format(new Date(workflow.created_at), 'MMM dd, yyyy HH:mm:ss')}
                      </p>
                    </div>

                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">Completed At</span>
                      </div>
                      <p className="text-white font-medium">
                        {workflow.completed_at
                          ? format(new Date(workflow.completed_at), 'MMM dd, yyyy HH:mm:ss')
                          : 'In Progress'}
                      </p>
                    </div>

                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <FileText className="w-4 h-4" />
                        <span className="text-sm">Total Accounts</span>
                      </div>
                      <p className="text-white font-medium">{workflow.total_accounts}</p>
                    </div>

                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Processed Accounts</span>
                      </div>
                      <p className="text-white font-medium">{workflow.processed_accounts}</p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {workflow.error_message && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-400">Error Message</p>
                          <p className="text-sm text-red-300 mt-1">{workflow.error_message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Spreadsheet URL */}
                  {workflow.spreadsheet_url && (
                    <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <p className="text-sm text-gray-400 mb-2">Spreadsheet URL</p>
                      <a
                        href={workflow.spreadsheet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-400 hover:text-orange-300 text-sm break-all"
                      >
                        {workflow.spreadsheet_url}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Steps Tab */}
              {activeTab === 'steps' && (
                <div className="space-y-3">
                  {steps.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No steps recorded</p>
                  ) : (
                    steps.map((step: any, index: number) => (
                      <div
                        key={index}
                        className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            {getStepIcon(step.status)}
                            <div className="flex-1">
                              <h3 className="font-medium text-white">{step.step_name}</h3>
                              <p className={`text-sm ${getStatusColor(step.status)}`}>
                                {step.status}
                              </p>
                              {step.error_message && (
                                <p className="text-sm text-red-400 mt-2">{step.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">Duration</p>
                            <p className="text-sm text-white">
                              {formatDuration(step.started_at, step.completed_at)}
                            </p>
                          </div>
                        </div>
                        {step.started_at && (
                          <div className="mt-3 pt-3 border-t border-zinc-700/50 flex justify-between text-xs text-gray-400">
                            <span>
                              Started: {format(new Date(step.started_at), 'MMM dd, HH:mm:ss')}
                            </span>
                            {step.completed_at && (
                              <span>
                                Completed: {format(new Date(step.completed_at), 'MMM dd, HH:mm:ss')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Events Tab */}
              {activeTab === 'events' && (
                <div className="space-y-2">
                  {events.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">No events recorded</p>
                  ) : (
                    events.map((event: any, index: number) => (
                      <div
                        key={index}
                        className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50 hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">{event.event_type}</p>
                            {event.event_data && (
                              <p className="text-xs text-gray-400 mt-1">
                                {JSON.stringify(event.event_data)}
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">
                            {format(new Date(event.created_at), 'HH:mm:ss')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 p-4 flex justify-end gap-2">
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this workflow? This will remove all data including events and steps.')) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowHistoryDetailModal;
